import { INVITE_NOTE_LIMIT, INVITE_NOTE_PREMIUM_LIMIT } from "./inviteNote.constants";

import type { LinkedinTier } from "@/utils/getLinkedinTier";

export type InviteNoteSeverity = "ok" | "warning" | "error";

/**
 * The hard character limit above which a LinkedIn invite note is rejected for a
 * given sender tier. Free senders are limited to the standard 200 characters;
 * Premium / Sales Navigator senders may use up to 300. An unknown tier is treated
 * leniently (premium limit) so we don't over-block before the tier is resolved.
 */
export const inviteNoteErrorLimit = (tier?: LinkedinTier): number =>
	tier === "free" ? INVITE_NOTE_LIMIT : INVITE_NOTE_PREMIUM_LIMIT;

/**
 * Resolves the validation severity of an invite note for a given sender tier.
 *
 * - **Free** senders: any non-empty note is a warning (they can only send ~5
 *   connection requests with a note per month), and over 200 characters is an error.
 * - **Premium / Sales Navigator** (and unknown tier): up to 200 characters is ok,
 *   201–300 is a warning, and over 300 is an error.
 */
export const getInviteNoteSeverity = (
	charCount: number,
	tier?: LinkedinTier
): InviteNoteSeverity => {
	const isFree = tier === "free";

	if (charCount > inviteNoteErrorLimit(tier)) {
		return "error";
	}

	const warningFloor = isFree ? 0 : INVITE_NOTE_LIMIT;

	if (charCount > warningFloor) {
		return "warning";
	}

	return "ok";
};
